import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, StatCard } from "@/components/ui";
import { money } from "@/lib/utils";
import { Users, Activity, DollarSign, Gauge } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { useBrandPalette } from "@/lib/brand";

const GROWTH = [
  { year: "2027", members: 134600 }, { year: "2028", members: 205000 },
  { year: "2029", members: 310000 }, { year: "2030", members: 420000 },
  { year: "2031", members: 538000 },
];

export default function Analytics() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState(null);
  const [claims, setClaims] = useState(null);
  const { palette, primary, dark } = useBrandPalette();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/quality-measures").then((r) => setQ(r.data));
    api.get("/claims-summary").then((r) => setClaims(r.data));
  }, []);

  if (!d || !q || !claims) return <Spinner label="Building analytics…" />;

  const qChart = q.map((m) => ({ name: m.measure_id, rate: m.rate }));

  return (
    <div className="vc-fade-up">
      <PageHeader title="Executive Analytics" subtitle="Real-time, data-driven insights into patient care and population health. Self-service reporting built in." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Managed Lives" value={d.total_members} icon={Users} tone="brand" />
        <StatCard label="High-Acuity" value={`${Math.round((d.high_risk / d.total_members) * 100)}%`} sub="Of population" icon={Activity} tone="red" />
        <StatCard label="Paid Claims Value" value={money(claims.total_paid)} icon={DollarSign} tone="blue" />
        <StatCard label="Assessment Rate" value={`${d.assessment_completion}%`} icon={Gauge} tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-lg mb-1">Scalability Model — Projected Enrollment</h3>
          <p className="text-xs text-muted-foreground mb-4">Platform architected for 300%+ organizational growth (starting at 134,600 members)</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={GROWTH}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={primary} stopOpacity={0.35} /><stop offset="95%" stopColor={primary} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Area type="monotone" dataKey="members" stroke={dark} strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Plan Mix</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.plan_distribution} dataKey="value" nameKey="name" outerRadius={90}>
                {d.plan_distribution.map((e, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Quality Measure Rates (HEDIS)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={qChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip /><Line type="monotone" dataKey="rate" stroke={primary} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Members by County</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.county_distribution} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="value" radius={[0, 4, 4, 0]} fill={palette[2]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
